/**
 * GpuScriptBacking — the player's `engine.gpu` v2 implementation (W-GPU parity).
 *
 * The player has NO Slang compiler: `loadKernel(guid)` resolves against the build-emitted
 * `compute-shaders.json` (`{ guid: { wgsl, reflection } }`, ExportComputeShadersStep), fetched
 * lazily on first use — the same pattern as VFXSubsystem's `ensureVFXShaders`. All device work
 * (explicit reflection-derived bind layouts, per-kernel uniforms, readback, device-loss) is the
 * shared render2d {@link ScriptComputeRuntime} — identical semantics to the editor backing.
 *
 * Containment contract (GpuBacking): nothing throws, loadKernel never rejects — failures resolve
 * dead handles with a message.
 */

import type { GpuBacking, GpuKernelHandle } from "@engine/core";
import {
  ScriptComputeRuntime,
  type ComputeModuleArtifact,
  type ComputeShaderReflection,
} from "@engine/render2d";

interface ManifestSource {
  /** Relative manifest path of compute-shaders.json, if the build exported any. */
  relPath: string | undefined;
  resolveUrl(rel: string): string;
}

type ComputeShaderMap = Record<string, { wgsl: string; reflection: ComputeShaderReflection }>;

export class GpuScriptBacking implements GpuBacking {
  private readonly _runtime: ScriptComputeRuntime;
  private _map: Promise<ComputeShaderMap> | null = null;

  constructor(
    getDevice: () => GPUDevice | null,
    private readonly _source: () => ManifestSource | null,
  ) {
    this._runtime = new ScriptComputeRuntime(getDevice, console);
  }

  /** Destroy every live GPU resource (app teardown backstop; per-script scopes free the rest). */
  dispose(): void {
    this._runtime.disposeAll();
  }

  isAvailable(): boolean {
    return this._runtime.isAvailable();
  }

  async loadKernel(shaderRef: string, kernelName?: string): Promise<GpuKernelHandle> {
    const dead = (error: string): GpuKernelHandle => {
      console.warn(`[gpu] loadKernel('${shaderRef}') failed: ${error}`);
      return { id: -1, ok: false, name: kernelName ?? shaderRef, numThreads: [1, 1, 1], resources: [], error };
    };
    try {
      const map = await this.ensureMap();
      const entry = map[shaderRef];
      if (!entry) return dead("compute shader not in the build (is the asset imported and valid?)");
      const artifact: ComputeModuleArtifact = { wgsl: entry.wgsl, reflection: entry.reflection };
      return await this._runtime.createKernel(shaderRef, artifact, kernelName);
    } catch (err) {
      return dead(err instanceof Error ? err.message : String(err));
    }
  }

  /** Fetch `compute-shaders.json` once (empty map when the build exported none / fetch fails). */
  private ensureMap(): Promise<ComputeShaderMap> {
    if (this._map) return this._map;
    this._map = (async () => {
      const source = this._source();
      if (!source?.relPath) return {};
      try {
        const resp = await fetch(source.resolveUrl(source.relPath));
        if (!resp.ok) return {};
        return (await resp.json()) as ComputeShaderMap;
      } catch (err) {
        console.error("[gpu] Failed to load compute-shaders.json:", err);
        return {};
      }
    })();
    return this._map;
  }

  // ---- Plain delegation to the shared runtime -------------------------------------------------

  destroyKernel(kernel: number): void {
    this._runtime.destroyKernel(kernel);
  }
  createBuffer(byteLength: number, label?: string): number {
    return this._runtime.createBuffer(byteLength, label);
  }
  writeBuffer(buffer: number, data: ArrayBufferView, byteOffset?: number): void {
    this._runtime.writeBuffer(buffer, data, byteOffset);
  }
  readBuffer(buffer: number, byteLength?: number, byteOffset?: number): Promise<ArrayBuffer | null> {
    return this._runtime.readBuffer(buffer, byteLength, byteOffset);
  }
  destroyBuffer(buffer: number): void {
    this._runtime.destroyBuffer(buffer);
  }
  createTexture(width: number, height: number, format?: string, label?: string): number {
    return this._runtime.createTexture(width, height, format, label);
  }
  destroyTexture(texture: number): void {
    this._runtime.destroyTexture(texture);
  }
  setBuffer(kernel: number, param: string, buffer: number): void {
    this._runtime.setBuffer(kernel, param, buffer);
  }
  setTexture(kernel: number, param: string, texture: number): void {
    this._runtime.setTexture(kernel, param, texture);
  }
  setUniform(kernel: number, param: string, value: number | ArrayLike<number>): void {
    this._runtime.setUniform(kernel, param, value);
  }
  dispatch(kernel: number, x: number, y?: number, z?: number): boolean {
    return this._runtime.dispatch(kernel, x, y, z);
  }
}
